import { Text, View } from '@react-pdf/renderer'
import { AnalysisMalariaReport } from '../../../models/analysis-malaria-report'
import moment from 'moment'
import { styles } from '../styles'
import { PARASITEMIA_THRESHOLD } from '../../../utils/constants'
import { useTranslation } from 'react-i18next'

const AiReportToPrint = ({
  analysisMalariaReport,
}: {
  analysisMalariaReport: AnalysisMalariaReport
}) => {
  const { t } = useTranslation()

  return (
    <View style={styles.mYmediumContainer}>
      <View style={styles.mediumMb}>
        <Text style={[styles.text, styles.boldText, styles.smallMb]}>
          {t('ai.malaria.report.title')}
        </Text>
        <Text style={styles.smallText}>
          {t('shared.common.updatedAt')}{' '}
          {moment(analysisMalariaReport.updatedAt).format('DD/MM/YYYY')}{' '}
          {t('shared.common.at')}{' '}
          {moment(analysisMalariaReport.updatedAt).format('HH:mm')}
        </Text>
      </View>

      <View style={[styles.gridContainer, styles.mediumMb]}>
        {/* Infected */}
        <View
          style={[
            styles.cardContainer,
            styles.reportStatCard,
            { borderLeftColor: '#E53E3E' },
          ]}
        >
          <Text
            style={[
              styles.smallText,
              styles.boldText,
              styles.smallMb,
              { color: '#718096' },
            ]}
          >
            {t('ai.malaria.report.totalInfectedRbc')}
          </Text>
          <Text style={[styles.boldText, { fontSize: 16 }]}>
            {analysisMalariaReport.infectedRbcCount}
          </Text>
        </View>

        {/* Uninfected  */}
        <View
          style={[
            styles.cardContainer,
            styles.reportStatCard,
            { borderLeftColor: '#38A169' },
          ]}
        >
          <Text
            style={[
              styles.smallText,
              styles.boldText,
              styles.smallMb,
              { color: '#718096' },
            ]}
          >
            {t('ai.malaria.report.totalUninfectedRbc')}
          </Text>
          <Text style={[styles.boldText, { fontSize: 16 }]}>
            {analysisMalariaReport.uninfectedRbcCount}
          </Text>
        </View>

        {/* Parasitaemia  */}
        <View
          style={[
            styles.cardContainer,
            styles.reportStatCard,
            { borderLeftColor: '#4299E1' },
          ]}
        >
          <Text
            style={[
              styles.smallText,
              styles.boldText,
              styles.smallMb,
              { color: '#718096' },
            ]}
          >
            {t('ai.malaria.report.estimatedParasitemia')}
          </Text>
          <View style={{ flexDirection: 'row', alignItems: 'center' }}>
            <Text style={[styles.boldText, { fontSize: 16 }]}>
              {analysisMalariaReport.estimatedParasitemiaPercent.toFixed(2)}%
            </Text>
            <View
              style={{
                backgroundColor:
                  analysisMalariaReport.estimatedParasitemiaPercent >
                  PARASITEMIA_THRESHOLD
                    ? '#FED7D7'
                    : '#C6F6D5',
                borderRadius: 4,
                padding: 2,
                marginLeft: 5,
              }}
            >
              <Text
                style={{
                  fontSize: 8,
                  color:
                    analysisMalariaReport.estimatedParasitemiaPercent >
                    PARASITEMIA_THRESHOLD
                      ? '#E53E3E'
                      : '#38A169',
                }}
              >
                {analysisMalariaReport.estimatedParasitemiaPercent >
                PARASITEMIA_THRESHOLD
                  ? t('ai.malaria.report.high')
                  : t('ai.malaria.report.low')}
              </Text>
            </View>
          </View>
        </View>
      </View>

      <View style={styles.table}>
        {/* Table Header */}
        <View style={[styles.tableRow, styles.headerRow]}>
          <View style={[styles.tableCell, styles.headerCell]}>
            <Text>{t('shared.common.createdAt')}</Text>
          </View>
          <View style={[styles.tableCell, styles.headerCell]}>
            <Text>{t('ai.malaria.report.infectedCount')}</Text>
          </View>
          <View style={[styles.tableCell, styles.headerCell]}>
            <Text>{t('ai.malaria.report.uninfectedCount')}</Text>
          </View>
          <View style={[styles.tableCell, styles.headerCell]}>
            <Text>{t('ai.malaria.report.estimatedParasitemia')} (%)</Text>
          </View>
        </View>

        {/* Table Data */}
        {analysisMalariaReport.diagnosticImages?.map((diagnosticImage) => (
          <View key={diagnosticImage.id} style={styles.tableRow}>
            <View style={[styles.tableCell]}>
              <Text>
                {moment(diagnosticImage.analysis?.createdAt).format(
                  'DD/MM/YYYY HH:mm'
                )}
              </Text>
            </View>
            <View style={styles.tableCell}>
              <Text>{diagnosticImage.analysis?.infectedRbcCount}</Text>
            </View>
            <View style={styles.tableCell}>
              <Text>{diagnosticImage.analysis?.uninfectedRbcCount}</Text>
            </View>
            <View style={styles.tableCell}>
              <Text>
                {diagnosticImage.analysis?.estimatedParasitemiaPercent.toFixed(
                  2
                )}
              </Text>
            </View>
          </View>
        ))}
      </View>

      {/* Caption */}
      <View>
        <Text style={styles.tableCaption}>
          {t('ai.malaria.report.tableCaptionMsg', {
            totalAnalyzedCells: analysisMalariaReport.totalAnalyzedCells,
            totalAnalysisImagesCount:
              analysisMalariaReport.totalAnalysisImagesCount,
          })}
        </Text>
      </View>
    </View>
  )
}

export default AiReportToPrint
